import { DRIVERS, DriverSpec, driverById } from './drivers';
import { POINTS, RacerSpec, RacerStatus } from './race';
import { RngState, rngNext } from './rng';
import { DEFAULT_TEAM, TEAMS, teamById } from './teams';
import { CALENDAR } from './tracks/index';

/**
 * The season (design.md A3, B6): six rounds in the original's order,
 * championship points, and the car-challenge system. Before each race a
 * driver may challenge another for their car; finish ahead and you take
 * it for the rest of the season. AI drivers covet better cars, and an AI
 * in a worse car that beats you takes yours (the original's sting).
 *
 * Pure data + deterministic rules: a saved season replays identically.
 */

export const PLAYER_ID = 'you';
export type Difficulty = 'rookie' | 'pro' | 'legend';
export type CrashRuleName = 'classic' | 'respawn';

/** AI field pace per difficulty (multiplies corner speeds). */
export const AI_PACE: Record<Difficulty, number> = { rookie: 1.12, pro: 1.28, legend: 1.42 };
/** Chance an eligible AI issues a challenge before a race. */
const CHALLENGE_BASE: Record<Difficulty, number> = { rookie: 0.22, pro: 0.32, legend: 0.42 };

export interface Challenge {
  challenger: string;
  defender: string;
}

export interface ChallengeOutcome extends Challenge {
  /** Driver id of the winner, or null for a draw (both retired). */
  winner: string | null;
  swapped: boolean;
  /** Teams before the race. */
  challengerTeam: string;
  defenderTeam: string;
}

export interface RoundEntry {
  driver: string;
  team: string;
  position: number;
  status: RacerStatus;
  points: number;
  timeTicks: number | null;
  bestLapTicks: number | null;
}

export interface RoundResult {
  track: string;
  entries: RoundEntry[];
  challenges: ChallengeOutcome[];
  fastestLap: string | null;
}

export interface SeasonState {
  v: 1;
  seed: number;
  difficulty: Difficulty;
  crashRule: CrashRuleName;
  /** Index of the next round to race (CALENDAR.length = season over). */
  round: number;
  /** driver id → team id. */
  seats: Record<string, string>;
  points: Record<string, number>;
  rounds: RoundResult[];
  /** Your challenge record against each AI driver. */
  rivalry: Record<string, { won: number; lost: number }>;
  /** Qualifying in progress for the current round (null until done). */
  pendingQual: (number | null)[] | null;
}

export function allDriverIds(): string[] {
  return [PLAYER_ID, ...DRIVERS.map((d) => d.id)];
}

export function newSeason(seed: number, difficulty: Difficulty, crashRule: CrashRuleName): SeasonState {
  const seats: Record<string, string> = { [PLAYER_ID]: DEFAULT_TEAM };
  const points: Record<string, number> = { [PLAYER_ID]: 0 };
  const rivalry: SeasonState['rivalry'] = {};
  for (const d of DRIVERS) {
    seats[d.id] = d.teamId;
    points[d.id] = 0;
    rivalry[d.id] = { won: 0, lost: 0 };
  }
  return { v: 1, seed: seed >>> 0, difficulty, crashRule, round: 0, seats, points, rounds: [], rivalry, pendingQual: null };
}

export function seasonOver(s: SeasonState): boolean {
  return s.round >= CALENDAR.length;
}

/** Racer specs for the current seats: you first, then the AI field. */
export function seasonSpecs(s: SeasonState): RacerSpec[] {
  const specs: RacerSpec[] = [{ id: PLAYER_ID, name: 'YOU', tag: 'YOU', team: teamById(s.seats[PLAYER_ID]) }];
  for (const d of DRIVERS) specs.push({ id: d.id, name: d.name, tag: d.tag, team: teamById(s.seats[d.id]), driver: d });
  return specs;
}

/**
 * Race-day specs: an AI driver who has issued a challenge is fired up for
 * it (a little quicker, a little more error-prone). Without this the
 * slower driver in the slower car almost never wins, and the seats freeze.
 */
export const FIRED_UP_PACE = 1.07;
export const FIRED_UP_MISTAKES = 1.6;
export function raceSpecs(specs: RacerSpec[], challenges: Challenge[]): RacerSpec[] {
  const hot = new Set(challenges.map((c) => c.challenger));
  return specs.map((sp) =>
    sp.driver && hot.has(sp.id)
      ? { ...sp, driver: { ...sp.driver, pace: sp.driver.pace * FIRED_UP_PACE, mistakeRate: sp.driver.mistakeRate * FIRED_UP_MISTAKES } }
      : sp,
  );
}

export function tierOf(s: SeasonState, driver: string): number {
  return teamById(s.seats[driver]).tier;
}

/** Who drives each team right now, in team order (fastest car first). */
export function teamSheet(s: SeasonState): { team: string; driver: string }[] {
  return [...TEAMS]
    .sort((a, b) => a.tier - b.tier)
    .map((t) => ({ team: t.id, driver: Object.keys(s.seats).find((d) => s.seats[d] === t.id) ?? '' }))
    .filter((x) => x.driver);
}

function roundRng(s: SeasonState, salt: number): RngState {
  return { seed: (s.seed ^ Math.imul(s.round + 1, 0x9e3779b1) ^ salt) >>> 0 };
}

/**
 * AI-issued challenges for the current round (design.md B6.2). Deterministic
 * for a given season + round, so reloading can't re-roll them. An AI only
 * challenges a driver in a better car, normally one or two places up the
 * order; you are a favourite target (from anywhere below), more so if
 * you've beaten that driver before.
 */
export function aiChallenges(s: SeasonState): Challenge[] {
  const rng = roundRng(s, 0xc4a1);
  const ais = DRIVERS.map((d) => d.id);
  // Shuffle the order AIs get to "speak" (Fisher-Yates on the round RNG).
  for (let i = ais.length - 1; i > 0; i--) {
    const j = Math.floor(rngNext(rng) * (i + 1));
    [ais[i], ais[j]] = [ais[j], ais[i]];
  }
  const engaged = new Set<string>();
  const out: Challenge[] = [];
  for (const id of ais) {
    if (engaged.has(id)) continue;
    const drv = driverById(id) as DriverSpec;
    const myTier = tierOf(s, id);
    const p = CHALLENGE_BASE[s.difficulty] * (0.5 + drv.aggression);
    if (rngNext(rng) >= p) continue;
    // AI drivers go for the car just above theirs (a fight they can win);
    // you are fair game from further down the order.
    const cands = allDriverIds().filter((o) => {
      if (o === id || engaged.has(o)) return false;
      const gap = myTier - tierOf(s, o);
      return gap > 0 && (gap <= 2 || o === PLAYER_ID);
    });
    if (cands.length === 0) continue;
    const weights = cands.map((o) => {
      const gap = myTier - tierOf(s, o);
      let w = gap === 1 ? 2 : 1;
      if (o === PLAYER_ID) w *= 1.6 + 0.4 * (s.rivalry[id]?.won ?? 0);
      return w;
    });
    let r = rngNext(rng) * weights.reduce((a, b) => a + b, 0);
    let pick = cands[0];
    for (let k = 0; k < cands.length; k++) {
      r -= weights[k];
      if (r <= 0) {
        pick = cands[k];
        break;
      }
    }
    out.push({ challenger: id, defender: pick });
    engaged.add(id);
    engaged.add(pick);
  }
  return out;
}

/** Drivers already in a challenge this round. */
export function engagedIn(challenges: Challenge[]): Set<string> {
  const e = new Set<string>();
  for (const c of challenges) {
    e.add(c.challenger);
    e.add(c.defender);
  }
  return e;
}

/**
 * Record a finished round: points, challenge outcomes and seat swaps.
 * `order` is driver ids in finishing order with each one's status.
 */
export function applyRound(
  s: SeasonState,
  trackId: string,
  order: { driver: string; status: RacerStatus; timeTicks: number | null; bestLapTicks: number | null }[],
  challenges: Challenge[],
): RoundResult {
  const entries: RoundEntry[] = order.map((o, i) => ({
    driver: o.driver,
    team: s.seats[o.driver],
    position: i + 1,
    status: o.status,
    points: POINTS[i] ?? 0,
    timeTicks: o.timeTicks,
    bestLapTicks: o.bestLapTicks,
  }));
  for (const e of entries) s.points[e.driver] = (s.points[e.driver] ?? 0) + e.points;

  let fastestLap: string | null = null;
  let best = Infinity;
  for (const e of entries) {
    if (e.bestLapTicks !== null && e.bestLapTicks < best) {
      best = e.bestLapTicks;
      fastestLap = e.driver;
    }
  }

  const pos = (id: string) => entries.find((e) => e.driver === id)!;
  const outcomes: ChallengeOutcome[] = [];
  for (const c of challenges) {
    const a = pos(c.challenger);
    const b = pos(c.defender);
    const challengerTeam = s.seats[c.challenger];
    const defenderTeam = s.seats[c.defender];
    let winner: string | null;
    if (a.status === 'out' && b.status === 'out') winner = null;
    else winner = a.position < b.position ? c.challenger : c.defender;
    const swapped = winner === c.challenger;
    if (swapped) {
      s.seats[c.challenger] = defenderTeam;
      s.seats[c.defender] = challengerTeam;
    }
    // Your head-to-head record (feeds AI grudges).
    const other = c.challenger === PLAYER_ID ? c.defender : c.defender === PLAYER_ID ? c.challenger : null;
    if (other && winner !== null && s.rivalry[other]) {
      if (winner === PLAYER_ID) s.rivalry[other].won++;
      else s.rivalry[other].lost++;
    }
    outcomes.push({ ...c, winner, swapped, challengerTeam, defenderTeam });
  }

  const result: RoundResult = { track: trackId, entries, challenges: outcomes, fastestLap };
  s.rounds.push(result);
  s.round++;
  s.pendingQual = null;
  return result;
}

/** Championship order: points, then wins, then best finishes, then fewest retirements. */
export function standings(s: SeasonState): { driver: string; points: number; wins: number; podiums: number }[] {
  const rows = allDriverIds().map((d) => {
    let wins = 0;
    let podiums = 0;
    let finishSum = 0;
    for (const r of s.rounds) {
      const e = r.entries.find((x) => x.driver === d);
      if (!e) continue;
      if (e.position === 1) wins++;
      if (e.position <= 3) podiums++;
      finishSum += e.position;
    }
    return { driver: d, points: s.points[d] ?? 0, wins, podiums, finishSum };
  });
  rows.sort((a, b) => b.points - a.points || b.wins - a.wins || b.podiums - a.podiums || a.finishSum - b.finishSum || a.driver.localeCompare(b.driver));
  return rows.map(({ driver, points, wins, podiums }) => ({ driver, points, wins, podiums }));
}

export function driverName(id: string): string {
  return id === PLAYER_ID ? 'YOU' : (driverById(id)?.name ?? id.toUpperCase());
}

export function driverTag(id: string): string {
  return id === PLAYER_ID ? 'YOU' : (driverById(id)?.tag ?? id.slice(0, 3).toUpperCase());
}
